import { useAuth0 } from '@auth0/auth0-react';
import Wallet from '@mui/icons-material/AccountBalanceWalletOutlined';
import Add from '@mui/icons-material/AddCircleOutlineOutlined';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import Support from '@mui/icons-material/ContactSupportOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import MenuIcon from '@mui/icons-material/MenuOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import logo from '@/assets/common/logo.webp';
import PaperPlaneIcon from '@/assets/common/PaperPlaneIcon';
import Button from '@/components/Button';
import CountryFlag from '@/components/Flags/CountryFlag.tsx';
import GenericDrawer from '@/components/GenericDrawer/index.tsx';
import { VITE_MASSIVE_LOAD_URL } from '@/constants/env.ts';
import useResponsive from '@/hooks/useResponsive.ts';
import RechargeBalance from '@/sections/RechargeBalance/index.tsx';
import { formatCurrency } from '@/utils/common';

import { AppBar as genAppBar, Drawer as genDrawer } from './dependencies.tsx';
import DrawerMenu from './DrawerMenu';
import { genClosedMixin, genOpenedMixin } from './mixing.ts';
import { ProfileMenuOptions } from './ProfileMenuOptions.tsx';
import useLayout from './useLayout.ts';

interface ILayout {
  children: React.ReactNode;
}

const drawerWidth = 260;
const openedMixin = genOpenedMixin(drawerWidth);
const closedMixin = genClosedMixin();

const AppBar = genAppBar(drawerWidth);
const Drawer = genDrawer(drawerWidth, openedMixin, closedMixin);

const Layout = (props: ILayout) => {
  const { getAccessTokenSilently } = useAuth0();
  const {
    handle_el,
    openDrawer,
    closeDrawerOnMobile,
    openBalanceDrawer,
    handleOpenUserMenu,
    handleCloseUserMenu,
    toggleDrawer,
    toggleBalanceDrawer,
    balance,
    profile,
    option,
    handleOption,
    isSmallScreen,
  } = useLayout();
  const theme = useTheme();
  const navigate = useNavigate();

  const isMobile = useResponsive('between', 'xxs', 'xxl');

  const [shippingMenuAnchor, setShippingMenuAnchor] = useState<null | HTMLElement>(null);
  const isShippingMenuOpen = Boolean(shippingMenuAnchor);

  const handleShippingMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShippingMenuAnchor(event.currentTarget);
  };

  const handleShippingMenuClose = () => {
    setShippingMenuAnchor(null);
  };

  return (
    <>
      <AppBar color="inherit" position="fixed" elevation={1} variant="outlined">
        <Toolbar>
          {isSmallScreen && (
            <IconButton
              size="large"
              edge="start"
              color="primary"
              onClick={toggleDrawer}
              sx={{
                transition: theme.transitions.create(['transform'], {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.short,
                }),
                transform: openDrawer ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            >
              {openDrawer ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          )}
          <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src={logo}
              style={{ maxHeight: 38, marginRight: '1rem', marginLeft: '1rem', cursor: 'pointer' }}
              alt="99Minutos logo"
            />
          </Link>
          <Box
            sx={{
              display: 'none',
              '@media (min-width: 600px)': {
                display: 'flex',
              },
              alignItems: 'center',
              flexGrow: 1,
              gap: { xs: '0.25rem', sm: '0.5rem', md: '1rem' },
            }}
          >
            {profile.data?.configuration.paymentType === 'PREPAID' && (
              <Box
                display="flex"
                justifyContent="center"
                gap="0.25rem"
                alignItems="center"
                bgcolor="#f6f6fb"
                padding={{ xxs: '0.2rem 0.4rem', md: '0.5rem' }}
                borderRadius="25px"
                sx={{
                  '&& strong': {
                    fontSize: { xxs: '0.7rem', sm: '0.8rem', md: '1rem' },
                  },
                }}
              >
                <Wallet sx={{ color: theme.palette.grey[500] }} fontSize="small" />
                {balance.loading ? (
                  <CircularProgress size={16} color="primary" />
                ) : (
                  <strong>{formatCurrency(balance.data, profile.data?.country)}</strong>
                )}
                <Add
                  sx={{ color: theme.palette.primary.main, cursor: 'pointer' }}
                  onClick={toggleBalanceDrawer}
                  fontSize="small"
                />
              </Box>
            )}
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Button
                sx={{ display: { xxs: 'none', xl: 'flex' } }}
                variant="contained"
                text="Haz un envío"
                startIcon={<PaperPlaneIcon />}
              />
            </Link>
            <Button
              sx={{ display: { xxs: 'none', xl: 'flex' } }}
              variant="outlined"
              text="Carga masiva de envíos"
              startIcon={<LocalShippingOutlined />}
              onClick={async () => {
                try {
                  const token = await getAccessTokenSilently();
                  const url = token
                    ? `${VITE_MASSIVE_LOAD_URL}?token=${encodeURIComponent(token)}`
                    : VITE_MASSIVE_LOAD_URL;
                  window.open(url, '_blank');
                } catch (error) {
                  console.error('Error fetching token for massive load:', error);
                  window.open(VITE_MASSIVE_LOAD_URL, '_blank');
                }
              }}
            />
            <Button
              sx={{
                display: { xxs: 'flex', xl: 'none' },
                fontSize: { xxs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                padding: { xxs: '4px 8px', sm: '6px 16px' },
                minWidth: 'auto',
              }}
              variant="contained"
              text="Enviar"
              startIcon={<PaperPlaneIcon sx={{ fontSize: '1.1rem' }} />}
              endIcon={<ArrowDropDown />}
              onClick={handleShippingMenuOpen}
            />
            <Menu
              anchorEl={shippingMenuAnchor}
              open={isShippingMenuOpen}
              onClose={handleShippingMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <MenuItem
                onClick={() => {
                  handleShippingMenuClose();
                  navigate('/');
                }}
              >
                <ListItemIcon>
                  <PaperPlaneIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="Haz un envío" />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleShippingMenuClose();
                  const token = localStorage.getItem('token');
                  const url = token
                    ? `${VITE_MASSIVE_LOAD_URL}?token=${encodeURIComponent(token)}`
                    : VITE_MASSIVE_LOAD_URL;
                  window.open(url, '_blank');
                }}
              >
                <ListItemIcon>
                  <LocalShippingOutlined fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="Carga masiva de envíos" />
              </MenuItem>
            </Menu>
          </Box>
          <Box sx={{ marginRight: 'auto' }}></Box>

          {!isMobile && (
            <Box display="flex" mr="1rem" alignItems="center">
              <ListItemButton
                component={Link}
                to="/support"
                sx={{
                  ':hover': { backgroundColor: theme.palette.primary.lighter },
                  padding: '0.5rem',
                  borderRadius: '1rem',
                }}
              >
                <Support color="primary" sx={{ mr: '0.5rem' }} />
                <strong style={{ color: theme.palette.secondary.main }}>FAQs</strong>
              </ListItemButton>
            </Box>
          )}

          {profile?.data?.profile?.firstName && (
            <Box gap="0.5rem" sx={{ display: { xxs: 'none', lg: 'flex' } }} mr="1rem">
              <strong style={{ color: theme.palette.secondary.main }}>Hola,</strong>{' '}
              <Typography
                color="primary"
                fontWeight={theme.typography.fontWeightMedium}
              >{`${profile.data.profile.firstName} ${profile.data.profile.lastName}`}</Typography>
            </Box>
          )}

          <IconButton
            size="large"
            color="primary"
            onClick={handleOpenUserMenu}
            sx={{ borderRadius: '1rem' }}
          >
            <CountryFlag country={profile?.data?.country ?? 'MEX'} />
            <ArrowDropDown />
          </IconButton>
          <Menu
            sx={{ mt: '45px' }}
            id="menu-appbar"
            anchorEl={handle_el}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={!!handle_el}
            onClose={handleCloseUserMenu}
          >
            <ProfileMenuOptions
              closeMenu={handleCloseUserMenu}
              option={option}
              onChgOption={handleOption}
            />
          </Menu>
        </Toolbar>
      </AppBar>
      <Box sx={{ display: 'flex' }}>
        <Drawer variant="permanent" open={openDrawer}>
          <DrawerMenu
            closeDrawerOnMobile={closeDrawerOnMobile}
            openDrawer={openDrawer}
            toggleDrawer={toggleDrawer}
            isSmallScreen={isSmallScreen}
            toggleBalanceDrawer={toggleBalanceDrawer}
          />
        </Drawer>
        <main
          style={{
            flex: 1,
            flexGrow: 1,
            overflowY: 'auto',
            scrollbarGutter: 'stable',
            paddingTop: 'calc(64px)',
            paddingBottom: '2rem',
            backgroundColor: '#FAFAFA',
            height: 'calc(100vh)',
          }}
        >
          <Container maxWidth="xxl">{props.children}</Container>
        </main>
      </Box>
      <GenericDrawer
        open={openBalanceDrawer}
        onClose={toggleBalanceDrawer}
        title={'Recargar saldo'}
        disableEscapeKeyDown={true}
        iconTitle={<Wallet color="primary" />}
      >
        <RechargeBalance />
      </GenericDrawer>
    </>
  );
};
export default Layout;
